import type { CSSProperties, ReactNode } from 'react';
import { Cursor, Stage, after } from './Stage';
import styles from './shape.module.css';

const COLS = [119, 283, 447];
const ROWS = [64, 186, 308];
// The first three chapters of the Duolingo case study, "What Duolingo does" cut to fit the margin.
const CHAPTERS = ['The problem', 'What it does', 'The ask'];

/**
 * The rows of the storyboard land one by one (1 to 3), then the chat comes up and asks (4, 5). The cursor
 * comes to the input and clicks it (6, 7, 8), the answer types itself in a letter at a time, then the cursor
 * goes to send, presses it, and the answer is sent. Then the cursor goes.
 */
const ANSWER = 'It asks for the account after a full lesson.';
const TYPE_FROM = 9;
const TYPED_AT = TYPE_FROM + ANSWER.length - 1;
const TO_SEND = TYPED_AT + 1;
const SENT = TO_SEND + 2;
// A moment after sending, the cursor goes.
const GONE = SENT + 1;
const MARKS = [
  200,
  480,
  760,
  1450,
  1800,
  2200,
  3100,
  3250,
  ...Array.from({ length: ANSWER.length }, (_, i) => 3450 + i * 34),
  5100,
  6000,
  6150,
  7100,
];
const ENTER_AT: [number, number] = [560, 470];
// Where the cursor's tip lands: in the input's text, then on the send button.
const INPUT_AT: [number, number] = [283, 393];
const SEND_AT: [number, number] = [457, 393];

const at = (col: number, row: number, d: number): CSSProperties => ({ left: COLS[col], top: ROWS[row], ...after(d) });

export function ShapeScene({ label }: { label: string }) {
  return (
    <Stage label={label} marks={MARKS} className={styles.scene}>
      {(step) => (
        <>
          <div className={styles.dots} />
          <div className={styles.header}>
            <span>Duolingo</span>
            <span className={styles.headerBar} />
          </div>

          {CHAPTERS.map((chapter, row) => (
            <div key={chapter} className={`${styles.chapter} ${step > row ? styles.in : ''}`} style={{ top: ROWS[row] + 10 }}>
              {chapter}
            </div>
          ))}

          <Frame row={0} step={step} style={at(0, 0, 0)} className={styles.donut}>
            <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden>
              <circle cx="28" cy="28" r="22" fill="none" stroke="rgb(41 41 41 / 10%)" strokeWidth="8" />
              <circle
                className={styles.donutArc}
                cx="28"
                cy="28"
                r="22"
                transform="rotate(-90 28 28)"
                fill="none"
                stroke="#84CCE8"
                strokeWidth="8"
                strokeLinecap="round"
              />
            </svg>
            <span className={styles.donutText}>
              <span className={styles.figure}>11</span>
              <span className={styles.bar} style={{ width: 44, height: 5 }} />
            </span>
          </Frame>

          <Frame row={0} step={step} style={at(1, 0, 80)} className={styles.lines}>
            {[100, 74, 46].map((w, i) => (
              <span key={w} className={styles.grow} style={{ width: `${w}%`, ...after(380 + i * 70) }} />
            ))}
            <span className={`${styles.grow} ${styles.pink}`} style={{ width: '20%', ...after(590) }} />
          </Frame>

          <Frame row={0} step={step} style={at(2, 0, 160)} className={styles.quote}>
            <span className={styles.mark}>&quot;</span>
            <span className={styles.quoteLines}>
              <span className={styles.grow} style={{ width: '100%', height: 5, ...after(420) }} />
              <span className={styles.grow} style={{ width: '70%', height: 5, ...after(480) }} />
            </span>
            <span className={styles.byline}>
              <span className={styles.face} />
              <span className={styles.bar} style={{ width: 40, height: 4 }} />
            </span>
          </Frame>

          <Frame row={1} step={step} style={at(0, 1, 0)} className={styles.change}>
            <span className={styles.before}>
              <i style={{ left: 5, top: 6, width: 14, height: 10 }} />
              <i style={{ left: 22, top: 12, width: 16, height: 8 }} />
              <i style={{ left: 8, top: 24, width: 20, height: 9 }} />
              <i style={{ left: 24, top: 36, width: 12, height: 12 }} />
              <i style={{ left: 6, top: 44, width: 12, height: 8 }} />
            </span>
            <svg className={styles.arrow} width="16" height="12" viewBox="0 0 16 14" aria-hidden>
              <path d="M1 7H14M9 2L14 7L9 12" fill="none" stroke="#292929" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className={styles.after}>
              <i className={styles.afterTop} />
              <span>
                <i />
                <i />
              </span>
              <span>
                <i />
                <i />
              </span>
            </span>
          </Frame>

          <div className={`${styles.slot} ${step > 1 ? styles.in : ''}`} style={at(1, 1, 80)}>
            <span className={styles.ping} />
            <svg width="32" height="32" viewBox="0 0 24 24" aria-hidden>
              <path
                d="M21 12C21 7.029 16.971 3 12 3C7.029 3 3 7.029 3 12C3 16.971 7.029 21 12 21C16.971 21 21 16.971 21 12ZM23 12C23 18.075 18.075 23 12 23C5.925 23 1 18.075 1 12C1 5.925 5.925 1 12 1C18.075 1 23 5.925 23 12Z"
                fill="#1D6177"
              />
              <path
                d="M9.6 9.4C9.6 8.07 10.67 7 12 7C13.33 7 14.4 8.07 14.4 9.4C14.4 10.45 13.73 11.05 13.02 11.55C12.43 11.97 12 12.38 12 13.1V13.6"
                fill="none"
                stroke="#1D6177"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11 16.99V16.98C11 16.43 11.45 15.98 12 15.98C12.55 15.98 13 16.43 13 16.98V16.99C13 17.54 12.55 17.99 12 17.99C11.45 17.99 11 17.54 11 16.99Z"
                fill="#1D6177"
              />
            </svg>
          </div>

          <Frame row={1} step={step} style={at(2, 1, 160)} className={styles.branch}>
            <svg width="122" height="76" viewBox="0 0 122 76" aria-hidden>
              <path
                d="M14 38C40 38 44 12 72 12"
                fill="none"
                stroke="rgb(41 41 41 / 22%)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeDasharray="3 4"
              />
              <path
                d="M14 38C40 38 44 64 72 64"
                fill="none"
                stroke="rgb(41 41 41 / 22%)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeDasharray="3 4"
              />
              <path
                className={styles.draw}
                pathLength={1}
                d="M14 38H72"
                fill="none"
                stroke="#479EBE"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="38" r="6" fill="#FBF9F6" stroke="#292929" strokeWidth="1.8" />
              <rect x="76" y="5" width="40" height="14" rx="4" fill="rgb(41 41 41 / 10%)" />
              <rect x="76" y="57" width="40" height="14" rx="4" fill="rgb(41 41 41 / 10%)" />
              <g className={styles.choice}>
                <rect x="76" y="30" width="40" height="16" rx="5" fill="#84CCE8" />
                <path
                  d="M90 38.2l3 2.8 5.6-5.8"
                  fill="none"
                  stroke="#292929"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </Frame>

          <Frame row={2} step={step} style={at(0, 2, 0)} className={styles.rise}>
            <span className={styles.figureSmall}>100%</span>
            <svg width="122" height="50" viewBox="0 0 122 50" aria-hidden>
              <path
                className={styles.area}
                d="M2 44C20 42 30 38 44 34C60 29 66 20 80 16C94 12 104 8 120 4V50H2Z"
                fill="rgb(132 204 232 / 22%)"
              />
              <path
                className={styles.draw}
                pathLength={1}
                d="M2 44C20 42 30 38 44 34C60 29 66 20 80 16C94 12 104 8 120 4"
                fill="none"
                stroke="#479EBE"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle className={styles.tip} cx="120" cy="4" r="3.5" fill="#479EBE" />
            </svg>
          </Frame>

          <Frame row={2} step={step} style={at(1, 2, 80)} className={styles.bars}>
            <span className={styles.barCol}>
              <span className={styles.barUp} style={{ height: 58, ...after(420) }} />
              <span className={styles.bar} style={{ width: 20, height: 4 }} />
            </span>
            <span className={styles.barCol}>
              <span className={`${styles.barUp} ${styles.tan}`} style={{ height: 36, ...after(520) }} />
              <span className={styles.bar} style={{ width: 20, height: 4 }} />
            </span>
            <span className={styles.drop}>6</span>
          </Frame>

          <Frame row={2} step={step} style={at(2, 2, 160)} className={styles.people}>
            <svg width="120" height="64" viewBox="0 0 120 64" aria-hidden>
              {Array.from({ length: 10 }, (_, i) => {
                const x = 12 + (i % 5) * 24;
                const y = i < 5 ? 10 : 42;
                return (
                  <g key={i} className={i < 6 ? styles.counted : undefined} style={after(420 + i * 70)}>
                    <circle cx={x} cy={y} r="5" />
                    <path d={`M${x - 8} ${y + 16}a8 8 0 0116 0v2H${x - 8}z`} />
                  </g>
                );
              })}
            </svg>
          </Frame>

          <div className={`${styles.chat} ${step >= 4 ? styles.up : ''}`}>
            <div className={styles.chatBody}>
              <div className={styles.chatTitle}>Chat</div>
              <div className={`${styles.ask} ${step >= 5 ? styles.in : ''}`}>What does Duolingo do differently?</div>
              <div className={`${styles.answerRow} ${step >= SENT ? styles.in : ''}`}>
                <div className={styles.answer}>
                  <span className={styles.answerText}>{ANSWER}</span>
                </div>
              </div>
              <div className={`${styles.input} ${step >= 8 && step < SENT ? styles.focused : ''}`}>
                {step >= TYPE_FROM && step < SENT ? (
                  <span className={styles.typed}>
                    <span className={styles.typedText}>{ANSWER.slice(0, step - TYPE_FROM + 1)}</span>
                    <span className={styles.caret} />
                  </span>
                ) : step === 8 ? (
                  <span className={styles.typed}>
                    <span className={styles.caret} />
                  </span>
                ) : (
                  <span className={styles.placeholder}>Ask a follow-up</span>
                )}
                <span className={`${styles.send} ${step === TO_SEND + 1 ? styles.pushed : ''}`}>
                  <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden>
                    <path
                      d="M6 10V2M2.5 5.5L6 2L9.5 5.5"
                      fill="none"
                      stroke="#FBF9F6"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <Cursor
            at={step >= TO_SEND ? SEND_AT : step >= 6 ? INPUT_AT : ENTER_AT}
            shown={step >= 6 && step < GONE}
            pressed={step === 7 || step === TO_SEND + 1}
          />
        </>
      )}
    </Stage>
  );
}

/** One page of the storyboard. It lands with its row, then whatever it shows draws itself. */
function Frame({
  row,
  step,
  style,
  className,
  children,
}: {
  row: number;
  step: number;
  style: CSSProperties;
  className: string;
  children: ReactNode;
}) {
  return (
    <div className={`${styles.frame} ${className} ${step > row ? styles.in : ''}`} style={style}>
      {children}
    </div>
  );
}
